// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xValidation } from "@stanion/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    phoneNumberView = "phoneNumberView",
    phoneNumber = "phoneNumber",
    email = "email",
    businessDays = "businessDays",
}

interface OwnProps {
    fields: {
        [fields.phoneNumberView]: string;
        [fields.phoneNumber]: string;
        [fields.email]: string;
        [fields.businessDays]: string;
    };
}

type Props = OwnProps;

interface FooterContactUsStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    phoneNumber?: LinkPresentationProps;
    businessDays?: TypographyPresentationProps;
    email?: LinkPresentationProps;
}

const footerContactUsStyles: FooterContactUsStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    title: {
        size: 16,
        lineHeight: "22px",
        weight: "bold",
        color: "text",
        css: css`
            letter-spacing: 0;
            margin-bottom: 8px;
        `,
    },
    phoneNumber: {
        typographyProps: {
            size: 14,
            lineHeight: "22px",
            css: css`
                letter-spacing: 0;
                margin-bottom: 4px;
            `,
        },
    },
    email: {
        typographyProps: {
            size: 12,
            lineHeight: "13px",
            css: css`
                letter-spacing: 0;
            `,
        },
    },
    businessDays: {
        size: 10,
        color: "text",
        lineHeight: "11px",
        css: css`
            margin-bottom: 8px;
        `,
    },
};

const styles = footerContactUsStyles;
const FooterContactUs: FC<Props> = ({ fields: { phoneNumberView, phoneNumber, email, businessDays } }) => (
    <StyledWrapper {...styles.container}>
        <Typography {...styles.title}>{translate("Contact Us")}</Typography>
        {phoneNumber && (
            <StyledWrapper>
                <Link href={`tel:${phoneNumber}`} {...styles.phoneNumber}>
                    {phoneNumberView || phoneNumber}
                </Link>
            </StyledWrapper>
        )}
        {businessDays && (
            <StyledWrapper>
                <Typography {...styles.businessDays}>{businessDays}</Typography>
            </StyledWrapper>
        )}
        {email && (
            <StyledWrapper>
                <Link href={`mailto:${email}`} {...styles.email}>
                    {email}
                </Link>
            </StyledWrapper>
        )}
    </StyledWrapper>
);

const widgetModule: WidgetModule = {
    component: FooterContactUs,
    definition: {
        displayName: "Footer - Contact Us",
        group: "Footer",
        fieldDefinitions: [
            {
                name: fields.phoneNumber,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                validate: value => {
                    if (!value) {
                        return null;
                    }
                    return xValidation.phone(value) ? null : "Invalid Phone number";
                },
            },
            {
                name: fields.phoneNumberView,
                displayName: "Phone number to view",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.businessDays,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.email,
                displayName: "Email",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                validate: value => {
                    if (!value) {
                        return null;
                    }
                    return xValidation.email(value) ? null : "Invalid Email";
                },
            },
        ],
    },
};

export default widgetModule;
